import { useState, useMemo, useCallback, useEffect } from "react";
import { usePagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/PaginationBar";
import { PermissionGate } from "@/components/PermissionGate";
import { useAppData } from "@/data/AppDataContext";
import { useInventory } from "@/data/InventoryContext";
import { useWarehouseInventory } from "@/data/WarehouseInventoryContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Link } from "react-router-dom";
import { BranchRegionFilter, EMPTY_BR, filterByBranchRegion, type BranchRegionState } from "@/components/BranchRegionFilter";

import {
  Package, AlertTriangle, Wifi, WifiOff,
  ArrowUpDown, ArrowUp, ArrowDown, TriangleAlert,
} from "lucide-react";
import type { StockLevel } from "@/data/inventoryAdapter";
import { getAllocationCoverage } from "@/data/inventory/allocationValidation";
import {
  CockpitPage, CockpitMasthead, HeroMetric, HeroDivider,
  CockpitFilterBar, CockpitSearch, CockpitTabs, CockpitChipGroup,
} from "@/components/cockpit/CockpitShell";
import { isUuid } from "@/lib/uuid";
import { backfillAllJobsMaterialsList } from "@/services/inventory.service";

type FilterKey = "all" | "missing" | "field-purchase" | "not-staged" | "fully-staged";
type StockFilter = "all" | "in-stock" | "low-stock" | "out-of-stock";
type StockSortField = "itemName" | "sku" | "quantityOnHand" | "quantityReserved" | "quantityAvailable" | "reorderPoint";
type SortDir = "asc" | "desc";

function getJobDisplayRef(job: { id: string; jobNumber?: unknown }): string {
  const jobNumber = typeof job.jobNumber === "string" ? job.jobNumber.trim() : "";
  return jobNumber || job.id;
}

function sanitizeMaterialLabel(value: string | null | undefined): string {
  const normalized = String(value ?? "").trim();
  if (!normalized) return "Unmapped Item";
  if (isUuid(normalized)) return "Unmapped Item";
  const raw = normalized as string;
  const withoutUuidTokens = raw
    .split(/\s+/)
    .filter((token) => !isUuid(token))
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
  return withoutUuidTokens || "Unmapped Item";
}

function SortIcon({ field, activeField, dir }: { field: string; activeField: string; dir: SortDir }) {
  if (field !== activeField) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-30" />;
  return dir === "asc" ? <ArrowUp className="h-3 w-3 ml-1" /> : <ArrowDown className="h-3 w-3 ml-1" />;
}

/* ── Stock Level Dashboard Tab ── */
function StockLevelDashboard() {
  const { snapshot, isConnected } = useInventory();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<StockFilter>("all");
  const [sortField, setSortField] = useState<StockSortField>("itemName");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const toggleSort = useCallback((field: StockSortField) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("asc"); }
  }, [sortField]);

  const counts = useMemo(() => {
    let inStock = 0, low = 0, out = 0;
    for (const item of snapshot.items) {
      if (item.status === "In Stock") inStock++;
      else if (item.status === "Low Stock") low++;
      else out++;
    }
    return { total: snapshot.items.length, inStock, low, out };
  }, [snapshot]);

  const filtered = useMemo(() => {
    let items = [...snapshot.items];
    if (filter === "in-stock") items = items.filter((i) => i.status === "In Stock");
    else if (filter === "low-stock") items = items.filter((i) => i.status === "Low Stock");
    else if (filter === "out-of-stock") items = items.filter((i) => i.status === "Out of Stock");

    if (search) {
      const s = search.toLowerCase();
      items = items.filter((i) => sanitizeMaterialLabel(i.itemName).toLowerCase().includes(s) || i.sku.toLowerCase().includes(s));
    }

    items.sort((a, b) => {
      const av = a[sortField], bv = b[sortField];
      if (typeof av === "number" && typeof bv === "number") return sortDir === "asc" ? av - bv : bv - av;
      return sortDir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return items;
  }, [snapshot, filter, search, sortField, sortDir]);

  const filterButtons: { key: StockFilter; label: string; count: number; color: string }[] = [
    { key: "all", label: "All", count: counts.total, color: "text-foreground" },
    { key: "in-stock", label: "In Stock", count: counts.inStock, color: "text-success" },
    { key: "low-stock", label: "Low Stock", count: counts.low, color: "text-warning" },
    { key: "out-of-stock", label: "Out of Stock", count: counts.out, color: "text-destructive" },
  ];

  const colHeaders: { field: StockSortField; label: string; center?: boolean }[] = [
    { field: "itemName", label: "Item Name" },
    { field: "sku", label: "SKU" },
    { field: "quantityOnHand", label: "On Hand", center: true },
    { field: "quantityReserved", label: "Reserved", center: true },
    { field: "quantityAvailable", label: "Available", center: true },
    { field: "reorderPoint", label: "Reorder Pt", center: true },
  ];

  return (
    <div className="space-y-4">
      {/* Hero KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 border-y border-border/60 divide-x divide-border/60">
        <div className="px-4 py-3"><HeroMetric label="Total SKUs" value={counts.total} /></div>
        <div className="px-4 py-3"><HeroMetric label="In Stock" value={counts.inStock} tone="good" /></div>
        <div className="px-4 py-3"><HeroMetric label="Low Stock" value={counts.low} tone={counts.low > 0 ? "warn" : "default"} /></div>
        <div className="px-4 py-3"><HeroMetric label="Out of Stock" value={counts.out} tone={counts.out > 0 ? "bad" : "default"} /></div>
      </div>

      {/* Filters + Search */}
      <CockpitFilterBar
        search={<CockpitSearch value={search} onChange={setSearch} placeholder="Search item or SKU..." />}
        chips={
          <CockpitChipGroup
            value={filter}
            onChange={(v) => setFilter(v as StockFilter)}
            options={filterButtons.map((b) => ({ value: b.key, label: b.label, count: b.count }))}
          />
        }
      />

      {/* Table */}
      {filtered.length === 0 ? (
        <p className="text-center text-xs text-muted-foreground py-10 border-y border-border/60">No items match your filters</p>
      ) : (
        <div className="overflow-x-auto border-y border-border/60">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border/60">
                {colHeaders.map((col) => (
                  <th
                    key={col.field}
                    onClick={() => toggleSort(col.field)}
                    className={`py-2.5 px-4 text-[10px] font-medium text-muted-foreground uppercase tracking-wider cursor-pointer select-none hover:text-foreground transition-colors ${col.center ? "text-center" : "text-left"}`}
                  >
                    <span className="inline-flex items-center">
                      {col.label}
                      <SortIcon field={col.field} activeField={sortField} dir={sortDir} />
                    </span>
                  </th>
                ))}
                <th className="py-2.5 px-4 text-[10px] font-medium text-muted-foreground uppercase tracking-wider text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {filtered.map((item) => (
                <tr key={item.sku} className="hover:bg-muted/20">
                  <td className="py-2.5 px-4 font-medium">{sanitizeMaterialLabel(item.itemName)}</td>
                  <td className="py-2.5 px-4 text-muted-foreground font-mono text-[10px] tabular-nums">{item.sku}</td>
                  <td className="py-2.5 px-4 text-center tabular-nums">{item.quantityOnHand}</td>
                  <td className="py-2.5 px-4 text-center tabular-nums">{item.quantityReserved}</td>
                  <td className="py-2.5 px-4 text-center tabular-nums">
                    <span className={item.quantityAvailable <= item.reorderPoint ? "text-warning font-semibold" : ""}>{item.quantityAvailable}</span>
                  </td>
                  <td className="py-2.5 px-4 text-center text-muted-foreground tabular-nums">{item.reorderPoint}</td>
                  <td className="py-2.5 px-4 text-center">
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${
                        item.status === "In Stock"
                          ? "bg-success/10 text-success border-success/20"
                          : item.status === "Low Stock"
                          ? "bg-warning/10 text-warning border-warning/20"
                          : "bg-destructive/10 text-destructive border-destructive/20"
                      }`}
                    >
                      {item.status === "Low Stock" && <AlertTriangle className="h-2.5 w-2.5 mr-1" />}
                      {item.status === "Out of Stock" && <TriangleAlert className="h-2.5 w-2.5 mr-1" />}
                      {item.status}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[10px] text-muted-foreground text-right">
        Source: {snapshot.source === "simulated" ? "Simulated Data" : "BoxHero"} · Last refreshed: {new Date(snapshot.lastRefreshedAt).toLocaleString()}
      </p>
    </div>
  );
}

/* ── Main Inventory Module ── */
export function InventoryModuleContent() {
  const { jobs, refreshData: refreshAppData, lastRefreshedAt } = useAppData();
  const { snapshot, isConnected, refresh: refreshInventory } = useInventory();
  const { jobAllocations } = useWarehouseInventory();
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<FilterKey>("all");
  const [brFilter, setBrFilter] = useState<BranchRegionState>(EMPTY_BR);
  const [tab, setTab] = useState<"job-materials" | "stock-levels">("job-materials");
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillResult, setBackfillResult] = useState<{ total: number; updated: number; failed: number } | null>(null);

  useEffect(() => {
    const refreshedAt = lastRefreshedAt ? Date.parse(lastRefreshedAt) : 0;
    const shouldRefreshAppData = !refreshedAt || Number.isNaN(refreshedAt) || (Date.now() - refreshedAt) > 60_000;

    if (shouldRefreshAppData) void refreshAppData();

    const inventoryRefreshedAt = snapshot.lastRefreshedAt ? Date.parse(snapshot.lastRefreshedAt) : 0;
    const shouldRefreshInventory =
      !inventoryRefreshedAt ||
      Number.isNaN(inventoryRefreshedAt) ||
      (Date.now() - inventoryRefreshedAt) > 60_000;

    if (shouldRefreshInventory) refreshInventory();
  }, [refreshAppData, refreshInventory, lastRefreshedAt, snapshot.lastRefreshedAt]);

  const activeJobs = filterByBranchRegion(
    jobs.filter((j) => j.currentStage !== "Completed"),
    brFilter
  );

  const jobsWithMaterials = activeJobs.filter((j) => j.materialsList && j.materialsList.length > 0);

  const getReq = (m: { quantityRequired?: number }) => Math.max(0, Number(m.quantityRequired ?? 0));
  const getAlloc = (m: { quantityAllocated?: number }) => Math.max(0, Number(m.quantityAllocated ?? 0));
  const getStaged = (m: { quantityStaged?: number }) => Math.max(0, Number(m.quantityStaged ?? 0));
  const getInstalled = (m: { quantityInstalled?: number }) => Math.max(0, Number(m.quantityInstalled ?? 0));
  const normSku = (value: unknown) => String(value ?? "").trim().toUpperCase().replace(/[^A-Z0-9]+/g, "");
  const keyFor = (jobId: string, sku: string) => `${jobId}::${normSku(sku)}`;
  const allocationCoverageByJobSku = useMemo(() => {
    const out = new Map<string, { required: number; allocated: number; shortBy: number; isShort: boolean }>();
    for (const job of jobsWithMaterials) {
      const coverage = getAllocationCoverage(job, jobAllocations);
      for (const row of coverage) {
        out.set(keyFor(job.id, row.sku), row);
      }
    }
    return out;
  }, [jobsWithMaterials, jobAllocations]);
  const liveAllocatedByJobSku = useMemo(() => {
    const out = new Map<string, number>();
    for (const alloc of jobAllocations) {
      if (alloc.status !== "Reserved" && alloc.status !== "Ready for Install") continue;
      const key = keyFor(String(alloc.jobId ?? ""), String(alloc.sku ?? ""));
      if (!key.includes("::") || key.endsWith("::")) continue;
      out.set(key, (out.get(key) ?? 0) + Math.max(0, Number(alloc.quantity ?? 0)));
    }
    return out;
  }, [jobAllocations]);
  const getLiveAlloc = (jobId: string, m: { sku?: string; quantityAllocated?: number }) => {
    const key = keyFor(jobId, String(m.sku ?? ""));
    const fromCoverage = allocationCoverageByJobSku.get(key)?.allocated ?? 0;
    const fromLive = liveAllocatedByJobSku.get(key) ?? 0;
    const fromLedger = getAlloc(m);
    return Math.max(fromLedger, fromCoverage, fromLive);
  };
  const getLiveReq = (jobId: string, m: { sku?: string; quantityRequired?: number }) => {
    const key = keyFor(jobId, String(m.sku ?? ""));
    const fromCoverage = allocationCoverageByJobSku.get(key)?.required ?? 0;
    return Math.max(getReq(m), fromCoverage);
  };
  const getMissing = (m: { quantityRequired?: number; quantityAllocated?: number; missingItems?: number }) =>
    Math.max(
      Math.max(0, Number(m.quantityRequired ?? 0) - Number(m.quantityAllocated ?? 0)),
      Math.max(0, Number(m.missingItems ?? 0)),
    );
  const getLiveMissing = (jobId: string, m: { sku?: string; quantityRequired?: number; quantityAllocated?: number }) => {
    const req = getLiveReq(jobId, m);
    const alloc = getLiveAlloc(jobId, m);
    return Math.max(0, req - alloc);
  };
  const getDerivedInventoryStatus = (m: {
    quantityRequired?: number;
    quantityAllocated?: number;
    quantityStaged?: number;
    quantityInstalled?: number;
    inventoryStatus?: "In Stock" | "Reserved" | "Allocated" | "Low Stock";
  }): "In Stock" | "Reserved" | "Allocated" | "Low Stock" => {
    const req = getReq(m);
    const alloc = getAlloc(m);
    const staged = getStaged(m);
    const installed = getInstalled(m);
    const missing = getMissing(m);
    if (missing > 0) return "Low Stock";
    if (installed >= req && req > 0) return "Allocated";
    if (staged > 0) return "Reserved";
    if (alloc > 0) return "Allocated";
    return m.inventoryStatus ?? "In Stock";
  };

  const missingCount = jobsWithMaterials.filter((j) =>
    j.materialsList!.some((m) => getLiveMissing(j.id, m) > 0)
  ).length;
  const fieldPurchaseCount = jobsWithMaterials.filter((j) =>
    j.materialsList!.some((m) => m.fieldPurchase)
  ).length;
  const notStagedCount = jobsWithMaterials.filter((j) =>
    j.materialsList!.some((m) => getStaged(m) < getReq(m))
  ).length;
  const fullyStagedCount = jobsWithMaterials.filter((j) =>
    j.materialsList!.every((m) => getStaged(m) >= getReq(m))
  ).length;

  const views: { key: FilterKey; label: string; count: number; color: string }[] = [
    { key: "all", label: "All Jobs", count: jobsWithMaterials.length, color: "text-foreground" },
    { key: "missing", label: "Missing Items", count: missingCount, color: "text-destructive" },
    { key: "field-purchase", label: "Field Purchase", count: fieldPurchaseCount, color: "text-warning" },
    { key: "not-staged", label: "Not Staged", count: notStagedCount, color: "text-info" },
    { key: "fully-staged", label: "Fully Staged", count: fullyStagedCount, color: "text-success" },
  ];

  let filtered = jobsWithMaterials;
  if (activeFilter === "missing") filtered = filtered.filter((j) => j.materialsList!.some((m) => getLiveMissing(j.id, m) > 0));
  if (activeFilter === "field-purchase") filtered = filtered.filter((j) => j.materialsList!.some((m) => m.fieldPurchase));
  if (activeFilter === "not-staged") filtered = filtered.filter((j) => j.materialsList!.some((m) => getStaged(m) < getReq(m)));
  if (activeFilter === "fully-staged") filtered = filtered.filter((j) => j.materialsList!.every((m) => getStaged(m) >= getReq(m)));

  if (search) {
    const s = search.toLowerCase();
    filtered = filtered.filter((j) =>
      j.id.toLowerCase().includes(s) ||
      getJobDisplayRef(j).toLowerCase().includes(s) ||
      j.customerName.toLowerCase().includes(s) ||
      j.materialsList!.some((m) => m.sku.toLowerCase().includes(s) || sanitizeMaterialLabel(m.itemName).toLowerCase().includes(s))
    );
  }
  const jobsPagination = usePagination(filtered, 15);
  useEffect(() => {
    jobsPagination.reset();
  }, [search, activeFilter, brFilter.branch, brFilter.region]);

  const totalItems = jobsWithMaterials.reduce((s, j) => s + j.materialsList!.length, 0);
  const totalMissing = jobsWithMaterials.reduce((s, j) => {
    return s + j.materialsList!.reduce((a, m) => {
      return a + getLiveMissing(j.id, m);
    }, 0);
  }, 0);
  const lowStockCount = jobsWithMaterials.reduce((s, j) => {
    return s + j.materialsList!.filter((m) => {
      return getLiveMissing(j.id, m) > 0;
    }).length;
  }, 0);
  const allocatedCount = jobsWithMaterials.reduce((s, j) => {
    return s + j.materialsList!.filter((m) => getLiveAlloc(j.id, m) > 0).length;
  }, 0);

  const handleBackfill = useCallback(async () => {
    setBackfillRunning(true);
    try {
      const result = await backfillAllJobsMaterialsList();
      setBackfillResult(result);
      await refreshAppData();
      refreshInventory();
    } finally {
      setBackfillRunning(false);
    }
  }, [refreshAppData, refreshInventory]);

  return (
    <div className="space-y-0">
      <CockpitMasthead
        eyebrow="Materials & Procurement · Inventory"
        title="Inventory"
        right={
          <div className="flex items-center gap-4">
            <PermissionGate permission="warehouse_edit">
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-[10px]"
                onClick={() => { void handleBackfill(); }}
                disabled={backfillRunning}
              >
                {backfillRunning ? "Backfilling..." : "Backfill Materials"}
              </Button>
            </PermissionGate>
            {backfillResult ? (
              <span className="text-[10px] text-muted-foreground tabular-nums">
                {backfillResult.updated}/{backfillResult.total} synced
                {backfillResult.failed > 0 ? ` · ${backfillResult.failed} failed` : ""}
              </span>
            ) : null}
            <HeroMetric label="SKUs" value={snapshot.items.length} />
            <HeroDivider />
            <HeroMetric label="Allocated" value={allocatedCount} />
            <HeroDivider />
            <HeroMetric label="Low Stock" value={lowStockCount} tone={lowStockCount > 0 ? "warn" : "default"} />
            <HeroDivider />
            <HeroMetric label="Missing" value={totalMissing} tone={totalMissing > 0 ? "bad" : "good"} />
            <HeroDivider />
            <Badge variant="outline" className={`gap-1.5 text-[10px] ${isConnected ? "text-success border-success/30" : "text-muted-foreground border-border"}`}>
              {isConnected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
              {isConnected ? "BoxHero" : "Simulated"}
            </Badge>
          </div>
        }
      />

      <CockpitTabs
        value={tab}
        onChange={(v) => setTab(v as typeof tab)}
        tabs={[
          { value: "job-materials", label: "Job Materials", count: jobsWithMaterials.length },
          { value: "stock-levels", label: "Stock Levels", count: snapshot.items.length },
        ]}
      />

      {tab === "job-materials" && (
        <div className="space-y-4">
          {/* Hero KPI strip */}
          <div className="grid grid-cols-2 sm:grid-cols-5 border-y border-border/60 divide-x divide-border/60">
            <div className="px-4 py-3"><HeroMetric label="Total SKUs" value={totalItems} /></div>
            <div className="px-4 py-3"><HeroMetric label="Allocated" value={allocatedCount} /></div>
            <div className="px-4 py-3"><HeroMetric label="Low Stock" value={lowStockCount} tone={lowStockCount > 0 ? "warn" : "default"} /></div>
            <div className="px-4 py-3"><HeroMetric label="Missing" value={totalMissing} tone={totalMissing > 0 ? "bad" : "good"} /></div>
            <div className="px-4 py-3"><HeroMetric label="Fully Staged" value={fullyStagedCount} tone="good" /></div>
          </div>

          {/* Filters + Search */}
          <CockpitFilterBar
            search={<CockpitSearch value={search} onChange={setSearch} placeholder="Search SKU, item, job, customer..." />}
            chips={
              <CockpitChipGroup
                value={activeFilter}
                onChange={(v) => setActiveFilter(v as FilterKey)}
                options={views.map((v) => ({ value: v.key, label: v.label, count: v.count }))}
              />
            }
            right={<BranchRegionFilter jobs={jobs} value={brFilter} onChange={setBrFilter} />}
          />

          {/* Job Materials Table */}
          {filtered.length === 0 ? (
            <p className="text-center text-xs text-muted-foreground py-10 border-y border-border/60">No inventory data matches your filters</p>
          ) : (
            <div className="overflow-x-auto border-y border-border/60">
              <table className="w-full text-xs border-separate border-spacing-0">
                <thead>
                  <tr>
                    <th className="text-left py-2.5 px-4 text-[10px] font-medium text-muted-foreground uppercase tracking-wider border-b border-border/60">Job</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-medium text-muted-foreground uppercase tracking-wider border-b border-border/60">Customer</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-medium text-muted-foreground uppercase tracking-wider border-b border-border/60">SKU</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-medium text-muted-foreground uppercase tracking-wider border-b border-border/60">Item</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-medium text-muted-foreground uppercase tracking-wider border-b border-border/60">Req</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-medium text-muted-foreground uppercase tracking-wider border-b border-border/60">Alloc</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-medium text-muted-foreground uppercase tracking-wider border-b border-border/60">Staged</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-medium text-muted-foreground uppercase tracking-wider border-b border-border/60">Installed</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-medium text-muted-foreground uppercase tracking-wider border-b border-border/60">Missing</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-medium text-muted-foreground uppercase tracking-wider border-b border-border/60">Status</th>
                  </tr>
                </thead>
                {jobsPagination.paged.map((j) => (
                  <tbody key={j.id} className="group/job transition-colors hover:[&_td]:bg-muted/30">
                    {j.materialsList!.map((m, idx) => {
                      const req = getLiveReq(j.id, m);
                      const alloc = getLiveAlloc(j.id, m);
                      const overAlloc = Math.max(0, alloc - req);
                      const staged = getStaged(m);
                      const installed = getInstalled(m);
                      const missing = Math.max(0, req - alloc);
                      const derivedStatus = missing > 0 ? "Low Stock" : alloc > 0 ? "Allocated" : getDerivedInventoryStatus(m);
                      const isFirst = idx === 0;
                      const isLast = idx === j.materialsList!.length - 1;
                      // Per-row ring: top on first row, bottom on last row, sides on every row
                      const ringTop = isFirst ? "border-t border-t-transparent group-hover/job:border-t-primary" : "";
                      const ringBottom = isLast ? "border-b border-b-border/60 group-hover/job:border-b-primary" : "";
                      const ringLeft = "border-l border-l-transparent group-hover/job:border-l-primary";
                      const ringRight = "border-r border-r-transparent group-hover/job:border-r-primary";
                      const innerDivider = idx > 0 ? "border-t border-t-border/30" : "";
                      // Merged Job/Customer cells span all rows — they always need top + bottom ring
                      const mergedRing = "border-y border-y-transparent group-hover/job:border-y-primary";
                      return (
                        <tr key={`${j.id}-${idx}`}>
                          {isFirst ? (
                            <>
                              <td
                                className={`py-2.5 px-4 align-top bg-muted/15 group-hover/job:bg-muted/40 transition-colors ${mergedRing} ${ringLeft}`}
                                rowSpan={j.materialsList!.length}
                              >
                                <Link to={`/jobs/${j.id}`} className="text-primary font-medium hover:underline tabular-nums">{getJobDisplayRef(j)}</Link>
                              </td>
                              <td
                                className={`py-2.5 px-4 font-medium align-top bg-muted/15 group-hover/job:bg-muted/40 transition-colors ${mergedRing}`}
                                rowSpan={j.materialsList!.length}
                              >
                                {j.customerName}
                              </td>
                            </>
                          ) : null}
                          <td className={`py-2.5 px-4 text-muted-foreground font-mono text-[10px] tabular-nums transition-colors ${innerDivider} ${ringTop} ${ringBottom}`}>{m.sku}</td>
                          <td className={`py-2.5 px-4 transition-colors ${innerDivider} ${ringTop} ${ringBottom}`}>{sanitizeMaterialLabel(m.itemName)}</td>
                          <td className={`py-2.5 px-4 text-center tabular-nums transition-colors ${innerDivider} ${ringTop} ${ringBottom}`}>{req}</td>
                          <td className={`py-2.5 px-4 text-center tabular-nums transition-colors ${innerDivider} ${ringTop} ${ringBottom}`}>
                            <span className={alloc < req || overAlloc > 0 ? "text-warning font-semibold" : ""}>
                              {alloc}
                              {overAlloc > 0 ? ` (+${overAlloc})` : ""}
                            </span>
                          </td>
                          <td className={`py-2.5 px-4 text-center tabular-nums transition-colors ${innerDivider} ${ringTop} ${ringBottom}`}>
                            <span className={staged < req ? "text-info font-semibold" : "text-success"}>{staged}</span>
                          </td>
                          <td className={`py-2.5 px-4 text-center tabular-nums transition-colors ${innerDivider} ${ringTop} ${ringBottom}`}>{installed}</td>
                          <td className={`py-2.5 px-4 text-center tabular-nums transition-colors ${innerDivider} ${ringTop} ${ringBottom}`}>
                            {missing > 0 ? (
                              <span className="text-destructive font-bold">{missing}</span>
                            ) : (
                              <span className="text-muted-foreground">0</span>
                            )}
                          </td>
                          <td className={`py-2.5 px-4 text-center transition-colors ${innerDivider} ${ringTop} ${ringBottom} ${ringRight}`}>
                            <div className="flex items-center justify-center gap-1">
                              <StatusBadge type="inventoryItem" value={derivedStatus} className="text-[10px]" />
                              {overAlloc > 0 ? (
                                <span className="text-[10px] text-warning">Over</span>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                ))}
              </table>
            </div>
          )}
          <PaginationBar {...jobsPagination} />
        </div>
      )}

      {tab === "stock-levels" && <StockLevelDashboard />}
    </div>
  );
}

export default function InventoryModule() {
  return (
    <CockpitPage>
      <InventoryModuleContent />
    </CockpitPage>
  );
}
